import {Entity} from "./entities/entity";
import {ArrayHelper} from "../helpers/array.helper";
import {ClassOf} from "../types/class-of.type";

export class EntityObjectArray<T extends Entity> extends Array {

    private readonly _allRemoved: T[] = [];
    private readonly _allAdded: T[] = [];

    constructor(_: number = 0, entities: T[] = [], added: T[] = [], removed: T[] = []) {
        super();
        super.push(...ArrayHelper.unique(entities));
        this._allAdded = ArrayHelper.uniqueEntities(ArrayHelper.unique(added));
        this._allRemoved = ArrayHelper.uniqueEntities(ArrayHelper.unique(removed));
    }

    public get allRemoved(): T[] {
        return this._allRemoved;
    }

    public get hasRemoved(): boolean {
        return this._allRemoved.length > 0;
    }

    public get entityClass(): ClassOf<T> | null {
        const firstEntity = ArrayHelper.first(this);
        if (!firstEntity) {
            return null;
        }

        return firstEntity.constructor as ClassOf<T>;
    }

    public static fromArray<T extends Entity>(entities: T[]): EntityObjectArray<T> {
        entities = ArrayHelper.uniqueEntities(entities);

        return new EntityObjectArray<T>(0, [...entities]);
    }

    public add(...entities: T[]): EntityObjectArray<T> {
        const oldLength = this.length;
        this.push(...entities);

        return oldLength !== this.length
            ? new EntityObjectArray<T>(0, [...this], this._allAdded, this._allRemoved)
            : this;
    }

    public remove(...entities: T[]): EntityObjectArray<T> {
        const oldLength = this.length;
        for (const entity of entities) {
            const idx = this.indexOf(entity);
            if (idx >= 0) {
                this.splice(idx, 1);
                if (!entity.isNew() && !this._allRemoved.includes(entity)) {
                    this._allRemoved.push(entity);
                }
            }
        }

        return this.length !== oldLength
            ? new EntityObjectArray<T>(0, [...this], this._allAdded, this._allRemoved)
            : this;
    }

    public push(...entities: T[]): number {
        const entityClass = this.entityClass;

        for (const entity of entities) {

            if (this.includes(entity)) {
                continue;
            }

            if (!!entityClass && this.entityClass !== entity.constructor) {
                continue;
            }

            super.push(entity);

            const idx = this._allRemoved.indexOf(entity);
            if (idx >= 0) {
                this._allRemoved.splice(idx, 1);
            } else {
                this._allAdded.push(entity);
            }
        }

        return this.length;
    }

    splice(start: number, deleteCount?: number): T[] {

        const deletedEntities = super.splice(start, deleteCount);
        for (const deletedEntity of deletedEntities) {
            if (this._allAdded.includes(deletedEntity))
                if (!this._allRemoved.includes(deletedEntity)) {
                    this._allRemoved.push(...deletedEntities);
                }

        }

        return deletedEntities;
    }
}